import React, { useState, useEffect, useRef } from 'react';
import { Modal, Input, Button, Spin, message, Alert } from 'antd';
import { SendOutlined, EditOutlined } from '@ant-design/icons';
import { getFollowUpDraft, sendFollowUp, getReplyDraft, sendReply } from '../../services/api';
import { ApiErrorCode, getApiErrorCode, getApiErrorMessage } from '../../utils/errorUtils';
import { AUTO_LANGUAGE } from '../../utils/languages';
import { captureEvent } from '../../services/analytics';
import LanguagePicker from '../localization/LanguagePicker';
import DraftLanguageChip from '../localization/DraftLanguageChip';
import css from './ComposeEmailModal.module.scss';

interface DraftLanguageMeta {
  language: string;
  languageName: string;
  reason: string;
}

const { TextArea } = Input;

type ComposeMode = 'follow_up' | 'reply';

interface ComposeEmailModalProps {
  applicationId: string;
  companyName: string;
  mode: ComposeMode;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  // Asked for when the send was refused over the recipient's address (D20):
  // the application's recommended action has moved on the server, and the
  // parent's refetch is what shows it (e.g. Retry Contact in place of Follow Up).
  onRefresh?: () => void;
}

const ComposeEmailModal: React.FC<ComposeEmailModalProps> = ({
  applicationId,
  companyName,
  mode,
  open,
  onClose,
  onSuccess,
  onRefresh,
}) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [footer, setFooter] = useState('');
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [sending, setSending] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  // Why the last send was refused over its recipient (D20), shown above the
  // draft in the server's words. `not_sendable` also blocks Send: this address
  // will never take the message, so only the way out is left.
  const [recipientBlock, setRecipientBlock] = useState<{
    kind: 'not_sendable' | 'pending';
    text: string;
  } | null>(null);
  // Language metadata of the current draft (both modes) + the user's
  // "Rewrite in…" selection (reply-only; follow-ups can't be re-drafted).
  const [draftLanguageMeta, setDraftLanguageMeta] = useState<DraftLanguageMeta | null>(null);
  const [rewriteLanguage, setRewriteLanguage] = useState<string>(AUTO_LANGUAGE);
  // Snapshot of the last server-provided draft, to detect unsaved edits before
  // a rewrite discards them.
  const pristineRef = useRef<{ body: string; footer: string }>({ body: '', footer: '' });
  // The server's follow-up subject, kept apart from pristineRef: a rewrite
  // (reply-only) never touches a subject, and the `draft_edited` analytics flag
  // counts body and footer only. It feeds the "keep my words" rule on a refused
  // recipient, where a rewritten subject is as much the user's work as the body.
  const pristineSubjectRef = useRef('');
  // The editor's values as of the latest render. The fields stay editable while
  // a send is in flight, so a refusal is judged against what is on screen when
  // it arrives, not the send-click render: words typed in the meantime are the
  // user's work too, and must keep the composer open.
  const latestDraftRef = useRef({ subject, body, footer });
  latestDraftRef.current = { subject, body, footer };
  // Guards against applying an in-flight rewrite result after unmount/close
  // (the open-effect has its own `cancelled` flag; doRewrite needs this). Setup
  // restores `true` so StrictMode's mount→unmount→remount leaves it correct.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Which opening of the composer is on screen. Bumped when it opens and again
  // when it closes or unmounts (the effect's cleanup), so a send can tell on
  // its answer whether the composer that sent it is still the one showing.
  // Both parents mount a fresh composer per target, and Cancel only closes it:
  // a send still in flight can answer while ANOTHER composer is open, and
  // its onClose would close that one, discarding its draft.
  const sessionRef = useRef(0);

  useEffect(() => {
    if (!open) return;

    sessionRef.current += 1;
    let cancelled = false;

    setSubject('');
    setBody('');
    setFooter('');
    setDraftError(null);
    setRecipientBlock(null);
    // A send from an earlier opening no longer clears this (see sessionRef).
    setSending(false);
    setDraftLanguageMeta(null);
    setRewriteLanguage(AUTO_LANGUAGE);
    pristineRef.current = { body: '', footer: '' };
    pristineSubjectRef.current = '';

    const fetchDraft = async () => {
      setLoadingDraft(true);
      try {
        if (mode === 'follow_up') {
          const draft = await getFollowUpDraft(applicationId);
          if (cancelled) return;
          setSubject(draft.subject);
          setBody(draft.body);
          setFooter(draft.footer);
          setDraftLanguageMeta({
            language: draft.language,
            languageName: draft.languageName,
            reason: draft.reason,
          });
          pristineRef.current = { body: draft.body, footer: draft.footer };
          pristineSubjectRef.current = draft.subject;
        } else {
          const draft = await getReplyDraft(applicationId);
          if (cancelled) return;
          setBody(draft.body);
          setFooter(draft.footer);
          setDraftLanguageMeta({
            language: draft.language,
            languageName: draft.languageName,
            reason: draft.reason,
          });
          pristineRef.current = { body: draft.body, footer: draft.footer };
        }
      } catch (error) {
        if (cancelled) return;
        const msg = getApiErrorMessage(error);
        setDraftError(msg);
      } finally {
        if (!cancelled) setLoadingDraft(false);
      }
    };

    fetchDraft();

    return () => {
      cancelled = true;
      sessionRef.current += 1;
    };
  }, [open, applicationId, mode]);

  // Re-draft the reply in a chosen language (ephemeral; replaces body/footer).
  const doRewrite = async (language: string) => {
    setLoadingDraft(true);
    try {
      const draft = await getReplyDraft(
        applicationId,
        language === AUTO_LANGUAGE ? undefined : language
      );
      if (!mountedRef.current) return;
      setBody(draft.body);
      setFooter(draft.footer);
      setDraftLanguageMeta({
        language: draft.language,
        languageName: draft.languageName,
        reason: draft.reason,
      });
      pristineRef.current = { body: draft.body, footer: draft.footer };
      // Commit the picker selection only once the new draft is applied, so a
      // failed rewrite leaves the picker on the language actually shown.
      setRewriteLanguage(language);
    } catch (error) {
      if (!mountedRef.current) return;
      // Non-destructive: keep the existing (possibly edited) draft visible and
      // just surface the error — a failed optional rewrite must not wipe work.
      message.error(getApiErrorMessage(error));
    } finally {
      if (mountedRef.current) setLoadingDraft(false);
    }
  };

  const handleRewriteChange = (language: string) => {
    if (language === rewriteLanguage) return;
    const dirty =
      body !== pristineRef.current.body || footer !== pristineRef.current.footer;
    if (dirty) {
      Modal.confirm({
        title: 'Replace your edits?',
        content:
          'Rewriting generates a new draft and discards your current edits. Continue?',
        okText: 'Rewrite',
        cancelText: 'Keep my edits',
        onOk: () => doRewrite(language),
      });
    } else {
      doRewrite(language);
    }
  };

  const handleSend = async () => {
    if (!body.trim()) {
      message.error('Email body cannot be empty');
      return;
    }
    if (mode === 'follow_up' && !subject.trim()) {
      message.error('Subject cannot be empty');
      return;
    }

    setSending(true);
    setRecipientBlock(null);
    const session = sessionRef.current;
    const isCurrent = () => sessionRef.current === session;
    const draftEdited =
      body !== pristineRef.current.body || footer !== pristineRef.current.footer;
    try {
      if (mode === 'follow_up') {
        await sendFollowUp(applicationId, {
          subject: subject.trim(),
          body: body.trim(),
          footer: footer.trim() || undefined,
        });
        message.success('Follow-up sent');
        captureEvent('follow_up_sent', {
          draft_edited: draftEdited,
          language: draftLanguageMeta?.language ?? null,
        });
      } else {
        await sendReply(applicationId, {
          body: body.trim(),
          footer: footer.trim() || undefined,
        });
        message.success('Reply sent');
        captureEvent('reply_sent', {
          draft_edited: draftEdited,
          language: draftLanguageMeta?.language ?? null,
        });
      }
      if (isCurrent()) {
        onClose();
        onSuccess();
      } else {
        // Sent, but from a composer that has since been closed; another may
        // be open now. Bring the page up to date without closing anything.
        onRefresh?.();
      }
    } catch (error) {
      const code = getApiErrorCode(error);
      if (!isCurrent()) {
        // The composer that sent this was closed while it was in flight.
        // Say what happened, and refresh the page over a refused recipient,
        // but leave whatever composer is on screen now alone.
        if (code === ApiErrorCode.ContactNotSendable || code === ApiErrorCode.ContactCheckPending) {
          message.warning(getApiErrorMessage(error));
          onRefresh?.();
        } else {
          message.error(getApiErrorMessage(error));
        }
        return;
      }
      if (code === ApiErrorCode.ContactNotSendable) {
        // This address can't receive mail, so this draft can never be sent
        // to it; the refreshed parent offers another contact. An untouched
        // draft just closes. An edited one stays open with the reason, so the
        // user's words aren't thrown away with it — including words typed
        // after Send was clicked (latestDraftRef).
        const latest = latestDraftRef.current;
        const editedNow =
          latest.body !== pristineRef.current.body ||
          latest.footer !== pristineRef.current.footer ||
          latest.subject !== pristineSubjectRef.current;
        if (editedNow) {
          setRecipientBlock({ kind: 'not_sendable', text: getApiErrorMessage(error) });
        } else {
          message.warning(getApiErrorMessage(error));
          onClose();
        }
        onRefresh?.();
      } else if (code === ApiErrorCode.ContactCheckPending) {
        // The address is being re-checked. Keep the draft open to send again
        // in a few minutes; the parent still refreshes to show where it stands.
        setRecipientBlock({ kind: 'pending', text: getApiErrorMessage(error) });
        onRefresh?.();
      } else {
        message.error(getApiErrorMessage(error));
      }
    } finally {
      // A later opening's own send owns the spinner.
      if (isCurrent()) setSending(false);
    }
  };

  const title = mode === 'follow_up' ? 'Follow Up' : 'Reply';

  return (
    <Modal
      title={
        <div className={css.modalTitle}>
          <EditOutlined />
          <span>{title} — {companyName}</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width="min(640px, 94vw)"
      destroyOnClose
      className={css.composeModal}
    >
      {loadingDraft ? (
        <div className={css.loadingContainer}>
          <Spin size="large" />
          <p>Generating draft with AI...</p>
        </div>
      ) : draftError ? (
        <Alert
          type="error"
          message="Could not generate draft"
          description={draftError}
          showIcon
        />
      ) : (
        <div className={css.form}>
          {recipientBlock && (
            <Alert
              type="warning"
              showIcon
              role="alert"
              message={
                recipientBlock.kind === 'not_sendable'
                  ? "This contact can't receive email"
                  : "We're re-checking this contact's address"
              }
              description={
                recipientBlock.kind === 'not_sendable' ? (
                  <>
                    <div>{recipientBlock.text}</div>
                    <div className={css.recipientBlockHint}>
                      Copy anything you want to keep before you close.
                    </div>
                  </>
                ) : (
                  recipientBlock.text
                )
              }
            />
          )}
          {mode === 'follow_up' && (
            <div className={css.field}>
              <div className={css.labelRow}>
                <label className={css.label}>Subject</label>
                {draftLanguageMeta && (
                  <DraftLanguageChip
                    language={draftLanguageMeta.language}
                    languageName={draftLanguageMeta.languageName}
                    reason={draftLanguageMeta.reason}
                  />
                )}
              </div>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject..."
                size="large"
                dir="auto"
              />
            </div>
          )}

          {mode === 'reply' && (
            <div className={css.replyLangRow}>
              {draftLanguageMeta && (
                <DraftLanguageChip
                  language={draftLanguageMeta.language}
                  languageName={draftLanguageMeta.languageName}
                  reason={draftLanguageMeta.reason}
                />
              )}
              <div className={css.rewriteControl}>
                <span className={css.rewriteLabel}>Rewrite in</span>
                <LanguagePicker
                  value={rewriteLanguage}
                  onChange={handleRewriteChange}
                  size="small"
                  ariaLabel="Rewrite in language"
                />
              </div>
            </div>
          )}

          <div className={css.field}>
            <label className={css.label}>Message</label>
            <TextArea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your message..."
              rows={10}
              className={css.bodyInput}
              dir="auto"
            />
          </div>

          <div className={css.footerField}>
            <label className={css.label}>Signature / Footer</label>
            <TextArea
              value={footer}
              onChange={(e) => setFooter(e.target.value)}
              placeholder="Your sign-off and contact info..."
              rows={3}
              className={css.footerInput}
              dir="auto"
            />
          </div>

          <div className={css.actions}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              loading={sending}
              disabled={
                !body.trim() ||
                (mode === 'follow_up' && !subject.trim()) ||
                recipientBlock?.kind === 'not_sendable'
              }
              className={css.sendButton}
            >
              Send {title}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ComposeEmailModal;
